import { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import can from 'ember-can/helpers/can';
import RouteTemplate from 'ember-route-template';
import { gt } from 'ember-truth-helpers';
import GroupsTable from 'garaje/components/location-overview/locations/groups-table';
import LocationsTableWithGroups from 'garaje/components/location-overview/locations/locations-table';
import LocationsMap from 'garaje/components/location-overview/locations/map';
import UiHeader from 'garaje/components/ui/header';
import UiPage from 'garaje/components/ui/page';
import PageSection from 'garaje/components/ui/page/section';
import UiPill from 'garaje/components/ui-pill';
import type AverageMonthlyVisitorCount from 'garaje/models/average-monthly-visitor-count';
import type GroupModel from 'garaje/models/group';
import type SkinnyLocation from 'garaje/models/skinny-location';
import type ProtectedController from 'garaje/pods/protected/controller';
import type LocationsService from 'garaje/services/locations';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import { filterBy } from 'macro-decorators';

import type { LocationOverviewLocationsRouteModelWithGroups } from './route';

interface LocationOverviewLocationsSignature {
  Args: { model: LocationOverviewLocationsRouteModelWithGroups };
}

class LocationOverviewLocationsTemplate extends Component<LocationOverviewLocationsSignature> {
  @service declare skinnyLocations: SkinnyLocationsService;
  @service declare locations: LocationsService;

  @filterBy('args.model.groups', 'isNew', false) declare groups: GroupModel[];

  @controller('protected') protectedController!: ProtectedController;

  get averageMonthlyVisitorCount(): AverageMonthlyVisitorCount[] {
    return this.args.model.averageMonthlyVisitorCount.slice();
  }

  @action
  switchLocation(location: SkinnyLocation, route: string): void {
    this.protectedController.send('switchLocation', location.id, route);
  }

  <template>
    <UiPage class="min-w-[900px]">
      <UiHeader>Locations</UiHeader>
      {{#if (can "manage locations-grouping")}}
        {{! Groups }}
        <PageSection>
          <div class="flex items-center">
            <div class="flex-auto">
              <h3>
                Groups
                {{#if (gt this.groups.length 0)}}
                  <span data-test-total-groups class="text-carbon-50 opacity-75">{{this.groups.length}}</span>
                {{else}}
                  <UiPill @fill="blue">New</UiPill>
                {{/if}}
              </h3>
              <p class="mt-2 mb-0 text-sm text-carbon-50">Use groups to organize your locations</p>
            </div>
            <LinkTo data-test-new-group @route="location-overview.locations.groups.new" class="btn btn--brand">
              Create group
            </LinkTo>
          </div>

          <GroupsTable data-test-groups-table @groups={{this.groups}} />
        </PageSection>

      {{/if}}

      {{! Locations }}
      <PageSection>
        <div class="flex items-center">
          <div class="flex-auto">
            <h3>Locations
              <span
                data-test-total-locations
                class="text-carbon-50 opacity-75"
              >{{this.skinnyLocations.content.length}}</span></h3>
            <p class="mt-2 mb-0 text-sm text-carbon-50">All locations in your company</p>
          </div>
          {{#if (can "create locations")}}
            <LinkTo data-test-new-location @route="new-location" class="btn btn--brand">
              Create location
            </LinkTo>
          {{/if}}
        </div>

        <LocationsMap data-test-locations-map @locations={{this.skinnyLocations.content}} />

        <LocationsTableWithGroups
          data-test-groups-locations-table
          @locations={{this.skinnyLocations.content}}
          @groups={{this.groups}}
          @averageMonthlyVisitorCount={{this.averageMonthlyVisitorCount}}
          @switchLocation={{this.switchLocation}}
          @canManageGroups={{can "manage locations-grouping"}}
        />
      </PageSection>
      {{outlet}}
    </UiPage>
  </template>
}

export default RouteTemplate(LocationOverviewLocationsTemplate);
